import * as React from 'react';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'moment';
import { RootState } from '../../reducers';
import {
    fetchNotifications,
    fetchMutedNotifications,
    removeMutedItemsBySource,
    markAllNotificationsRead,
    markNotificationRead,
    toggleStarNotification,
    snoozeNotification,
    bulkMarkRead,
    bulkToggleStar,
    bulkSnooze,
    checkForNewNotifications,
    mergePendingNotifications,
} from '../../actions/notifications';
import { getUserPermissionsAndResponsibilities } from '../../actions/generalActions';
import { axiosCaller } from '../../utils/apiCaller';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import { NotificationItem } from '../../reducers/notifications';
import NotificationDrawer from './NotificationDrawer';
import NotificationRowMenu, { MuteRequest } from './NotificationRowMenu';
import NotificationBulkToolbar from './NotificationBulkToolbar';
import NotificationSidebar, { StatusFilter, DateBucket } from './NotificationSidebar';
import NotificationTabs, { Tab } from './NotificationTabs';
import WebPushBanner from './WebPushBanner';
import { getCategoryMeta } from './categoryMeta';
import './NotificationsArchive.scss';

const SEARCH_DEBOUNCE_MS = 250;
const SKELETON_ROWS = 5;

/**
 * Bucket a notification's created_at into one of four date groups. We use
 * moment so locale-aware comparisons stay consistent with how the rest of the
 * app renders times.
 */
function bucketForDate(iso: string, now: moment.Moment): DateBucket {
    const m = moment(iso);
    if (!m.isValid()) return 'earlier';
    if (m.isSame(now, 'day')) return 'today';
    const yesterday = now.clone().subtract(1, 'day');
    if (m.isSame(yesterday, 'day')) return 'yesterday';
    if (m.isSameOrAfter(now.clone().startOf('isoWeek'))) return 'thisWeek';
    return 'earlier';
}

function isSnoozedNow(item: NotificationItem, now: moment.Moment): boolean {
    return !!item.muted_until && moment(item.muted_until).isAfter(now);
}

// ── Repetition collapsing ────────────────────────────────────────────────
// Background: the same user can fire many similar notifications in a short
// window (e.g. @kreshnik mentions you 7 times in "Marrëveshja: Fakon Ramadani
// familma" while editing the same comment thread). On mobile this stacks
// into a wall of identical rows that buries other notifications. Collapsing
// keeps the most recent visible and shows "+N" so the user knows there are
// more without scrolling past them.
//
// "Similar" = same source entity (source_type + source_id) AND same author
// (triggered_by) AND consecutive in the (date-sorted) list AND within 15 min.
// Tighter window than the bucket so legitimately spread-out activity stays
// visible.

const COLLAPSE_WINDOW_MS = 15 * 60 * 1000;
const COLLAPSE_MIN_GROUP = 3;

type RenderItem =
    | { kind: 'single'; row: NotificationItem }
    | { kind: 'group'; head: NotificationItem; members: NotificationItem[]; groupKey: string };

function groupKeyFor(item: NotificationItem): string | null {
    const sourceType = item.event?.source_type ?? '';
    const sourceId = item.event?.source_id ?? null;
    const authorId = item.event?.triggered_by?.id ?? null;
    if (!sourceType || sourceId == null || authorId == null) return null;
    return `${sourceType}:${sourceId}:${authorId}`;
}

function collapseRepeats(rows: NotificationItem[]): RenderItem[] {
    const out: RenderItem[] = [];
    let i = 0;
    while (i < rows.length) {
        const head = rows[i];
        const headKey = groupKeyFor(head);
        const members: NotificationItem[] = [head];

        if (headKey) {
            const headTime = new Date(head.created_at).getTime();
            let j = i + 1;
            while (j < rows.length) {
                const candidate = rows[j];
                const candidateKey = groupKeyFor(candidate);
                if (candidateKey !== headKey) break;
                const dt = Math.abs(headTime - new Date(candidate.created_at).getTime());
                if (dt > COLLAPSE_WINDOW_MS) break;
                members.push(candidate);
                j++;
            }

            if (members.length >= COLLAPSE_MIN_GROUP) {
                out.push({ kind: 'group', head, members, groupKey: headKey });
                i = j;
                continue;
            }
        }

        // Either no group key (e.g. project.created has no source entity), or
        // fewer than COLLAPSE_MIN_GROUP similar rows — render as singles.
        for (const m of members) {
            out.push({ kind: 'single', row: m });
        }
        i += members.length;
    }
    return out;
}

const BUCKET_ORDER: DateBucket[] = ['today', 'yesterday', 'thisWeek', 'earlier'];
const BUCKET_LABEL_ID: Record<DateBucket, string> = {
    today: 'notifications.archive.groupToday',
    yesterday: 'notifications.archive.groupYesterday',
    thisWeek: 'notifications.archive.groupThisWeek',
    earlier: 'notifications.archive.groupEarlier',
};
const BUCKET_DEFAULT_LABEL: Record<DateBucket, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    earlier: 'Earlier',
};

const SkeletonRow: React.FC = () => (
    <li className="notifications-archive__row notifications-archive__row--skeleton" aria-hidden="true">
        <span className="notifications-archive__indicator" />
        <span className="notifications-archive__row-icon notifications-archive__row-icon--skeleton" />
        <div className="notifications-archive__row-body">
            <div className="skeleton skeleton--title" />
            <div className="skeleton skeleton--text" />
            <div className="skeleton skeleton--text skeleton--text-short" />
        </div>
        <div className="skeleton skeleton--meta" />
    </li>
);

/**
 * Show a themed error toast (dark rose container, red icon chip, red progress
 * bar). Used by every catch block in this file so failures read visually
 * different from successes without forcing the user to read the text first.
 *
 * `intl` is passed in rather than read from context so the helper can be a
 * plain function instead of a hook — keeps call sites at the catch line.
 */
function showMikaErrorToast(intl: any, messageId: string, defaultMessage: string) {
    toast.error(
        <div className="mika-toast-content">
            <span className="mika-toast-content__icon" aria-hidden="true">
                {/* X-in-circle — the lucide/feather error glyph. */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
            </span>
            <div className="mika-toast-content__text">
                {intl.formatMessage({ id: messageId, defaultMessage })}
            </div>
        </div>,
        { className: 'mika-toast mika-toast--error' },
    );
}

const EmptyState: React.FC<{ messageId: string; defaultMessage: string }> = ({ messageId, defaultMessage }) => (
    <div className="notifications-archive__empty">
        <svg
            className="notifications-archive__empty-art"
            xmlns="http://www.w3.org/2000/svg"
            width="120"
            height="120"
            viewBox="0 0 120 120"
            fill="none"
            aria-hidden="true"
        >
            <circle cx="60" cy="60" r="56" fill="#f0f6ff" />
            <path d="M40 56a20 20 0 0 1 40 0v14l6 6H34l6-6V56z" fill="#fff" stroke="#1e3a5f" strokeWidth="2" strokeLinejoin="round" />
            <path d="M54 80a6 6 0 0 0 12 0" stroke="#1e3a5f" strokeWidth="2" strokeLinecap="round" fill="none" />
            <circle cx="80" cy="44" r="8" fill="#ff6b3d" />
            <path d="M76 44h8M80 40v8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <p className="notifications-archive__empty-text">
            <FormattedMessage id={messageId} defaultMessage={defaultMessage} />
        </p>
    </div>
);

/**
 * Render a colored circular badge for the notification's category, using the
 * SVG path from categoryMeta. The whole row gets dim color-coding here; the
 * full event-type granularity (completed/blocked/returned glyphs) can come in
 * a later pass once we surface event_type_key on the API response.
 */
const CategoryIcon: React.FC<{ category: string | null }> = ({ category }) => {
    const meta = getCategoryMeta(category);
    return (
        <span
            className="notifications-archive__row-icon"
            style={{ background: meta.color }}
            aria-hidden="true"
        >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
                <path d={meta.iconPath} />
            </svg>
        </span>
    );
};

// Safety-net reconciliation interval — 60s, only runs if Echo missed
// something (network drop, reconnect lag). Echo is the primary live channel;
// this poll is the belt+suspenders fallback.
const POLL_NEW_INTERVAL_MS = 60_000;

const NotificationsArchivePage: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const intl = useIntl();
    const items = useSelector((s: RootState) => s.notifications.items);
    const unreadCount = useSelector((s: RootState) => s.notifications.unreadCount);
    const loading = useSelector((s: RootState) => s.notifications.loading);
    const pendingNew = useSelector((s: RootState) => s.notifications.pendingNew);
    const mutedItems = useSelector((s: RootState) => s.notifications.mutedItems);
    const mutedLoading = useSelector((s: RootState) => s.notifications.mutedLoading);
    const locale = useSelector((s: RootState) => s.app?.locale) || 'en';
    const isAuthenticated = useSelector((s: RootState) => (s.user as any)?.isAuthenticated);

    // ── Filters ───────────────────────────────────────────────────────────
    // Tab is the primary segmentation (Inbox / Pinned / Snoozed). Sidebar
    // filters narrow within the current tab — they're orthogonal dimensions,
    // not redundant. E.g. "Pinned + Unread + Production + Today" is valid.
    const [tab, setTab] = useState<Tab>('inbox');
    const [sidebarStatus, setSidebarStatus] = useState<StatusFilter>('all');
    const [sidebarCategory, setSidebarCategory] = useState<string | null>(null);
    const [sidebarDateBucket, setSidebarDateBucket] = useState<DateBucket | null>(null);

    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const debounceRef = useRef<number | null>(null);

    // ── Mute source ───────────────────────────────────────────────────────
    // Per-session optimistic filter: when a user mutes Client X, any visible
    // notifications about Client X disappear immediately. The backend mute
    // suppresses future events AND filters list calls (NotificationRepository::
    // applyMuteFilter), so on next refetch the server returns the same clean
    // inbox — this local set is just for instant feedback before the
    // round-trip completes.
    //
    // Key format: "EntityType:id" e.g. "App\Models\Project:1277". Set lives
    // only on this page; clearing on unmount is fine because the next fetch
    // already excludes muted sources.
    const [muteFilterKeys, setMuteFilterKeys] = useState<Set<string>>(new Set());

    // Repetition-collapse: which group keys the user has expanded. When
    // a (source_type, source_id, author) group of ≥3 consecutive similar
    // rows collapses to one "+N" row, tapping the +N pill flips its key
    // here and the group expands inline. Per-session, not persisted —
    // collapse is a visual aid, not a setting.
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const toggleGroupExpand = useCallback((groupKey: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    }, []);

    // Two-pronged fetch on mount: the inbox (default `items`) AND the muted
    // set (separate `mutedItems` slot). We have to fetch muted up-front so the
    // "Të heshtura / Muted" tab badge is accurate from page load — otherwise
    // it shows 0 until the user clicks the tab, which makes the tab
    // undiscoverable for the people who most need it (those with active mutes).
    const load = useCallback(() => {
        (dispatch as any)(fetchNotifications(50));
        (dispatch as any)(fetchMutedNotifications(100));
    }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (dispatch as any)(getUserPermissionsAndResponsibilities());
    }, [dispatch]);

    // ── Live polling for new items ────────────────────────────────────────
    // Track the high-water-mark timestamp we've seen — initially the max
    // created_at from the first load, advanced when the user merges pending
    // items in. The poll asks for "anything created after this point" and
    // stages results in `pendingNew` until the user clicks the pill.
    //
    // Pauses when the tab is hidden so we don't burn API calls in the
    // background — Page Visibility API. Resumes immediately on visibility.
    const lastFetchAtRef = useRef<string>(new Date().toISOString());

    useEffect(() => {
        if (items.length === 0) return;
        const max = items.reduce<string>(
            (acc, i) => (i.created_at > acc ? i.created_at : acc),
            lastFetchAtRef.current,
        );
        lastFetchAtRef.current = max;
        // Only run when the list size changes (initial load + merge events).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const tick = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            (dispatch as any)(checkForNewNotifications(lastFetchAtRef.current));
        };
        const timer = window.setInterval(tick, POLL_NEW_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [dispatch, isAuthenticated]);

    const handleLoadPending = useCallback(() => {
        dispatch(mergePendingNotifications() as any);
    }, [dispatch]);

    useEffect(() => {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
            setSearchQuery(searchInput.trim().toLowerCase());
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
        };
    }, [searchInput]);

    // Source-of-truth filter: strip muted items BEFORE anything counts or
    // renders against them. Without this, tab + sidebar counts would still
    // include rows that aren't visible — "Inbox 12" with 8 visible items
    // looks broken. After the next refetch the server-side mute filter
    // catches up and `items` no longer contains muted rows; until then this
    // local filter keeps every derived view internally consistent.
    const isItemMuted = useCallback((item: NotificationItem): boolean => {
        if (muteFilterKeys.size === 0) return false;
        if (!item.event?.source_type || item.event?.source_id == null) return false;
        return muteFilterKeys.has(`${item.event.source_type}:${item.event.source_id}`);
    }, [muteFilterKeys]);

    const visibleItems = useMemo(
        () => (muteFilterKeys.size === 0 ? items : items.filter((i) => !isItemMuted(i))),
        [items, muteFilterKeys, isItemMuted],
    );

    const visiblePending = useMemo(
        () => (muteFilterKeys.size === 0 ? pendingNew : pendingNew.filter((i) => !isItemMuted(i))),
        [pendingNew, muteFilterKeys, isItemMuted],
    );

    // Counts include both the visible items AND the pending (live-arrived)
    // items so the sidebar + tabs feel as live as the bell badge. The
    // visible LIST below still uses `visibleItems` only — the pill stays visible
    // until the user clicks it, but the numbers don't lag behind reality.
    const allItemsForCounts = useMemo(() => [...visibleItems, ...visiblePending], [visibleItems, visiblePending]);

    // Tab counts run against the FULL item set (not the filtered subset),
    // so users see how many items exist in each tab regardless of which
    // sidebar buckets are active. Muted count comes from a separate Redux
    // slot (mutedItems) populated by its own fetch.
    const tabCounts = useMemo(() => {
        const now = moment();
        let inboxCount = 0, pinnedCount = 0, snoozedCount = 0;
        for (const item of allItemsForCounts) {
            const snoozed = isSnoozedNow(item, now);
            if (snoozed) snoozedCount++;
            else inboxCount++;
            if (item.starred) pinnedCount++;
        }
        return {
            inbox: inboxCount,
            pinned: pinnedCount,
            snoozed: snoozedCount,
            muted: mutedItems.length,
        };
    }, [allItemsForCounts, mutedItems.length]);

    // Items in the current tab — pre-sidebar-filter. Sidebar counts derive
    // from THIS set so each bucket count is "how many of the current tab fall
    // into this bucket." Most tabs use `visibleItems` (mute-filtered) as the
    // base; the Muted tab uses the separate `mutedItems` slot since those rows
    // are intentionally excluded from `items` by the server's mute filter.
    const itemsInTab = useMemo(() => {
        const now = moment();
        if (tab === 'muted') return mutedItems;
        if (tab === 'pinned') return visibleItems.filter((i) => i.starred);
        if (tab === 'snoozed') return visibleItems.filter((i) => isSnoozedNow(i, now));
        // Inbox excludes currently-snoozed items so workers don't see what
        // they explicitly told the app to hide.
        return visibleItems.filter((i) => !isSnoozedNow(i, now));
    }, [visibleItems, mutedItems, tab]);

    // Same tab-scope filter applied to the count source so sidebar buckets
    // include pending-but-not-yet-merged items. Muted tab counts directly
    // off mutedItems (no pending equivalent — Echo broadcasts are blocked
    // before they reach the user when a mute is active).
    const itemsInTabForCounts = useMemo(() => {
        const now = moment();
        if (tab === 'muted') return mutedItems;
        if (tab === 'pinned') return allItemsForCounts.filter((i) => i.starred);
        if (tab === 'snoozed') return allItemsForCounts.filter((i) => isSnoozedNow(i, now));
        return allItemsForCounts.filter((i) => !isSnoozedNow(i, now));
    }, [allItemsForCounts, mutedItems, tab]);

    const sidebarCounts = useMemo(() => {
        const now = moment();
        const byCategory: Record<string, number> = {};
        const byDate: Record<DateBucket, number> = { today: 0, yesterday: 0, thisWeek: 0, earlier: 0 };
        let unread = 0, starred = 0;
        for (const item of itemsInTabForCounts) {
            if (!item.read_at) unread++;
            if (item.starred) starred++;
            const cat = item.category || 'other';
            byCategory[cat] = (byCategory[cat] || 0) + 1;
            byDate[bucketForDate(item.created_at, now)]++;
        }
        return { all: itemsInTabForCounts.length, unread, starred, byCategory, byDate };
    }, [itemsInTabForCounts]);

    // Final filtered list: sidebar status + category + date + search.
    // Mute is applied upstream via `visibleItems` so we don't repeat it here.
    const filteredItems = useMemo(() => {
        const now = moment();
        const q = searchQuery;
        return itemsInTab.filter((item) => {
            if (sidebarStatus === 'unread' && item.read_at) return false;
            if (sidebarStatus === 'starred' && !item.starred) return false;
            if (sidebarCategory && (item.category || 'other') !== sidebarCategory) return false;
            if (sidebarDateBucket && bucketForDate(item.created_at, now) !== sidebarDateBucket) return false;
            if (q) {
                const hay = `${item.rendered_title || ''} ${item.rendered_body || ''}`.toLowerCase();
                if (hay.indexOf(q) === -1) return false;
            }
            return true;
        });
    }, [itemsInTab, sidebarStatus, sidebarCategory, sidebarDateBucket, searchQuery]);

    // Group filtered items by date for rendering.
    const grouped = useMemo(() => {
        const now = moment();
        const buckets: Record<DateBucket, NotificationItem[]> = {
            today: [], yesterday: [], thisWeek: [], earlier: [],
        };
        for (const item of filteredItems) {
            buckets[bucketForDate(item.created_at, now)].push(item);
        }
        return buckets;
    }, [filteredItems]);

    const totalVisible = filteredItems.length;
    // Muted tab has its own loading slot since it's fetched separately.
    const inMutedTab = tab === 'muted';
    const isEmpty = !((inMutedTab ? mutedLoading : loading)) && totalVisible === 0;
    const showSkeleton = inMutedTab
        ? (mutedLoading && mutedItems.length === 0)
        : (loading && items.length === 0);

    // Drop stale sidebar filters when the tab changes — staying on "Production
    // only" after switching from Inbox to Snoozed would usually show zero
    // results, which feels broken.
    useEffect(() => {
        setSidebarCategory(null);
        setSidebarDateBucket(null);
        setSidebarStatus('all');
    }, [tab]);

    // Lazy-fetch muted items the first time the user opens the Muted tab.
    // We refetch every time to keep it fresh — mutes are rare actions so the
    // extra round-trip is negligible, and stale display is more confusing
    // than a brief loader.
    useEffect(() => {
        if (tab === 'muted') {
            (dispatch as any)(fetchMutedNotifications(100));
        }
    }, [tab, dispatch]);

    // Also clear bulk selection when any filter dimension shifts the visible
    // set — otherwise a "ghost selection" persists on items no longer shown.
    useEffect(() => {
        setSelectedIds(new Set());
    }, [tab, sidebarStatus, sidebarCategory, sidebarDateBucket, searchQuery]);

    const handleItemClick = (item: NotificationItem) => {
        if (!item.read_at) {
            (dispatch as any)(markNotificationRead(item.id));
        }
        setSelectedId(item.id);
    };

    const handleDrawerClose = useCallback(() => {
        setSelectedId(null);
    }, []);

    const selectedItem = useMemo(
        () => (selectedId !== null ? items.find((i) => i.id === selectedId) ?? null : null),
        [items, selectedId],
    );

    const handleMarkAllRead = () => {
        (dispatch as any)(markAllNotificationsRead());
    };

    // Smart absolute time on rows — today renders HH:mm only, this year renders
    // DD MMM, HH:mm, older renders DD MMM YYYY. Hover still surfaces the full
    // unambiguous timestamp via the existing title attribute.
    const fmtExact = (iso: string): string => {
        const m = moment(iso);
        if (!m.isValid()) return '';
        const localized = m.locale(locale);
        const now = moment();
        if (localized.isSame(now, 'day')) return localized.format('HH:mm');
        if (localized.isSame(now, 'year')) return localized.format('DD MMM, HH:mm');
        return localized.format('DD MMM YYYY');
    };

    const fmtAbs = (iso: string): string => {
        const m = moment(iso);
        if (!m.isValid()) return '';
        return m.locale(locale).format('DD MMM YYYY, HH:mm');
    };

    const handleStarToggle = useCallback((id: number) => {
        (dispatch as any)(toggleStarNotification(id));
    }, [dispatch]);

    const handleSnooze = useCallback((id: number, until: string | null) => {
        (dispatch as any)(snoozeNotification(id, until));
    }, [dispatch]);

    const undoMute = useCallback(async (muteId: number, filterKey: string) => {
        try {
            await axiosCaller('DELETE', `notifications/mutes/${muteId}`);
            setMuteFilterKeys((prev) => {
                const next = new Set(prev);
                next.delete(filterKey);
                return next;
            });
            // Refetch to bring back any items that were filtered server-side.
            (dispatch as any)(fetchNotifications(50));
        } catch (e) {
            showMikaErrorToast(intl, 'notifications.mute.toast.error', 'Could not undo. Try again.');
        }
    }, [dispatch, intl]);

    /**
     * Unmute from the Muted tab. The mute row is keyed by (entity_type,
     * entity_id) — we don't have the mute's primary key on the notification
     * itself, so we use the legacy DELETE /mutes route (body-based) here.
     * After success: sweep matching items out of mutedItems immediately so the
     * Muted tab feels responsive, then refetch the inbox so the items return.
     */
    const handleUnmuteSource = useCallback(async (item: NotificationItem) => {
        const entityType = item.event?.source_type ?? null;
        const entityId = item.event?.source_id ?? null;
        if (!entityType || entityId == null) return;

        try {
            await axiosCaller('DELETE', 'notifications/mutes', {
                entity_type: entityType,
                entity_id: entityId,
            });
            // Optimistic sweep of mutedItems so other rows from the same source
            // also disappear from the Muted tab in one action.
            (dispatch as any)(removeMutedItemsBySource(entityType, entityId));
            // Bring items back into the inbox + sync unread badge.
            (dispatch as any)(fetchNotifications(50));
            toast.success(
                <div className="mika-toast-content">
                    <span className="mika-toast-content__icon" aria-hidden="true">
                        {/* Plain bell — universal "notifications on" glyph. */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                    </span>
                    <div className="mika-toast-content__text">
                        <FormattedMessage
                            id="notifications.mute.toast.unmuteSuccess"
                            defaultMessage="Source unmuted"
                        />
                    </div>
                </div>,
                { autoClose: 6000, className: 'mika-toast mika-toast--success' },
            );
        } catch (e) {
            showMikaErrorToast(intl, 'notifications.mute.toast.error', 'Could not unmute. Try again.');
        }
    }, [dispatch, intl]);

    const handleMute = useCallback(async (item: NotificationItem, req: MuteRequest) => {
        const filterKey = `${req.entityType}:${req.entityId}`;

        // Optimistic: hide everything from that source NOW, before the network round-trip.
        setMuteFilterKeys((prev) => {
            const next = new Set(prev);
            next.add(filterKey);
            return next;
        });

        // Defensive truncation — column is VARCHAR(191). Templates like
        // "Production process completed for client X / Project Y with beam"
        // can blow past that. Trim with a small buffer rather than letting
        // validation 422 the request and confuse the user.
        const safeLabel = req.sourceLabel ? req.sourceLabel.substring(0, 180) : null;

        try {
            const res: any = await axiosCaller('POST', 'notifications/mutes', {
                entity_type: req.entityType,
                entity_id: req.entityId,
                source_label: safeLabel,
                until: req.until,
            });
            const muteId = res?.data?.data?.id;

            // Refetch so server-side filter applies (sync tab/sidebar counts +
            // bell badge to what's actually visible). The local muteFilterKeys
            // remains active as a no-op safety net until the server response lands.
            (dispatch as any)(fetchNotifications(50));

            // Toast with Undo. Captured toast ID so the Undo handler can dismiss
            // it explicitly — react-toastify's closeOnClick doesn't reliably
            // close when the target is a stopPropagation'd button.
            const durationKey = `notifications.mute.duration.${req.duration}`;
            const durationDefaults: Record<string, string> = {
                '1hour': 'For 1 hour',
                'tomorrow': 'Until tomorrow 8 AM',
                '1week': 'For 1 week',
                'forever': 'Forever',
            };

            let createdToastId: any = null;
            createdToastId = toast.success(
                <div className="mika-toast-content">
                    <span className="mika-toast-content__icon" aria-hidden="true">
                        {/* Bell with slash — universal "muted" glyph. */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
                            <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
                            <path d="M18 8a6 6 0 0 0-9.33-5" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                    </span>
                    <div className="mika-toast-content__text">
                        <FormattedMessage
                            id="notifications.mute.toast.success"
                            defaultMessage="Muted {source} · {duration}"
                            values={{
                                source: safeLabel || `${req.entityType.split('\\').pop()} #${req.entityId}`,
                                duration: intl.formatMessage({
                                    id: durationKey,
                                    defaultMessage: durationDefaults[req.duration] || req.duration,
                                }),
                            }}
                        />
                    </div>
                    {muteId && (
                        <button
                            type="button"
                            className="mika-toast-content__action"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (createdToastId !== null) toast.dismiss(createdToastId);
                                undoMute(muteId, filterKey);
                            }}
                        >
                            <FormattedMessage id="notifications.mute.toast.undo" defaultMessage="Undo" />
                        </button>
                    )}
                </div>,
                // 10s window — fast enough that toasts don't pile up if the
                // user rapidly mutes several sources, slow enough to read and
                // click Undo without panic. The Muted tab remains the
                // discoverable recovery path after the toast fades.
                { autoClose: 10000, closeOnClick: false, className: 'mika-toast mika-toast--success' },
            );
        } catch (e) {
            // Revert the optimistic filter on failure so the user isn't left
            // staring at a stale empty list.
            setMuteFilterKeys((prev) => {
                const next = new Set(prev);
                next.delete(filterKey);
                return next;
            });
            showMikaErrorToast(intl, 'notifications.mute.toast.error', 'Could not mute. Try again.');
        }
    }, [dispatch, intl, undoMute]);

    const toggleSelection = useCallback((id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const handleBulkMarkRead = useCallback((ids: number[]) => {
        (dispatch as any)(bulkMarkRead(ids));
    }, [dispatch]);

    const handleBulkToggleStar = useCallback((ids: number[]) => {
        (dispatch as any)(bulkToggleStar(ids));
    }, [dispatch]);

    const handleBulkSnooze = useCallback((ids: number[], until: string | null) => {
        (dispatch as any)(bulkSnooze(ids, until));
    }, [dispatch]);

    const handleSelectAllVisible = useCallback(() => {
        setSelectedIds(new Set(filteredItems.map((i) => i.id)));
    }, [filteredItems]);

    const selectedItems = useMemo(
        () => filteredItems.filter((i) => selectedIds.has(i.id)),
        [filteredItems, selectedIds],
    );

    // Master checkbox in the head bar — toggles between select-all-visible and
    // clear. Indeterminate state requires DOM access (React doesn't expose the
    // `indeterminate` property on the JSX prop set).
    const headMasterRef = useRef<HTMLInputElement>(null);
    const headMasterChecked = filteredItems.length > 0 && selectedIds.size === filteredItems.length;
    const headMasterPartial = selectedIds.size > 0 && selectedIds.size < filteredItems.length;

    useEffect(() => {
        if (!headMasterRef.current) return;
        headMasterRef.current.indeterminate = headMasterPartial;
    }, [headMasterPartial]);

    const handleHeadMasterClick = useCallback(() => {
        if (selectedIds.size > 0) {
            clearSelection();
        } else {
            handleSelectAllVisible();
        }
    }, [selectedIds.size, clearSelection, handleSelectAllVisible]);

    const renderRow = (item: NotificationItem) => {
        const isSnoozed = isSnoozedNow(item, moment());
        const isSelected = selectedIds.has(item.id);
        return (
            <li
                key={item.id}
                className={`notifications-archive__row ${item.read_at ? '' : 'is-unread'} ${item.starred ? 'is-starred' : ''} ${isSnoozed ? 'is-snoozed' : ''} ${isSelected ? 'is-selected' : ''}`}
                onClick={() => handleItemClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleItemClick(item); }}
            >
                <label
                    className="notifications-archive__row-check"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={intl.formatMessage({ id: 'notifications.bulk.selectRow', defaultMessage: 'Select notification' })}
                >
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(item.id)}
                    />
                </label>
                <span className="notifications-archive__indicator" aria-hidden="true" />
                <CategoryIcon category={item.category} />
                <div className="notifications-archive__row-body">
                    <div className="notifications-archive__row-title">
                        {item.starred && <span className="notifications-archive__star" aria-hidden="true">★</span>}
                        {item.rendered_title}
                    </div>
                    <div className="notifications-archive__row-text">{item.rendered_body}</div>
                    {isSnoozed && (
                        <div className="notifications-archive__row-snooze" title={fmtAbs(item.muted_until!)}>
                            <FormattedMessage
                                id="notifications.row.snoozedUntil"
                                defaultMessage="Snoozed until {when}"
                                values={{ when: moment(item.muted_until!).locale(locale).calendar() }}
                            />
                        </div>
                    )}
                </div>
                <div className="notifications-archive__row-right">
                    <div className="notifications-archive__row-meta" title={fmtAbs(item.created_at)}>
                        {fmtExact(item.created_at)}
                    </div>
                    <NotificationRowMenu
                        item={item}
                        onStarToggle={handleStarToggle}
                        onSnooze={handleSnooze}
                        onMute={inMutedTab ? undefined : handleMute}
                        onUnmute={inMutedTab ? handleUnmuteSource : undefined}
                    />
                </div>
            </li>
        );
    };

    const renderGroup = (bucket: DateBucket) => {
        const rows = grouped[bucket];
        if (rows.length === 0) return null;

        // Collapse runs of ≥3 consecutive rows from the same author about the
        // same entity within 15 minutes. Keeps the most recent visible and
        // hides the rest behind a "+N more" pill. Per-group expansion state
        // lives in `expandedGroups` above. See collapseRepeats() at the top.
        const items = collapseRepeats(rows);

        return (
            <div key={bucket} className="notifications-archive__group">
                <div className="notifications-archive__group-label">
                    <span className="notifications-archive__group-pill">
                        <span className="notifications-archive__group-pill-dot" aria-hidden="true" />
                        <FormattedMessage
                            id={BUCKET_LABEL_ID[bucket]}
                            defaultMessage={BUCKET_DEFAULT_LABEL[bucket]}
                        />
                        <span className="notifications-archive__group-count">
                            {rows.length}
                        </span>
                    </span>
                    <span className="notifications-archive__group-rule" aria-hidden="true" />
                </div>
                <ul className="notifications-archive__list">
                    {items.map((it) => {
                        if (it.kind === 'single') return renderRow(it.row);
                        const isOpen = expandedGroups.has(it.groupKey);
                        const extraCount = it.members.length - 1;
                        if (isOpen) {
                            // Render all members + a "Show less" footer.
                            return (
                                <React.Fragment key={`group-${it.groupKey}`}>
                                    {it.members.map(renderRow)}
                                    <li
                                        className="notifications-archive__group-collapse"
                                        onClick={() => toggleGroupExpand(it.groupKey)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleGroupExpand(it.groupKey); }}
                                    >
                                        <FormattedMessage
                                            id="notifications.archive.groupShowLess"
                                            defaultMessage="Show less"
                                        />
                                    </li>
                                </React.Fragment>
                            );
                        }
                        // Collapsed — render only the head row, then a "+N more" pill row.
                        return (
                            <React.Fragment key={`group-${it.groupKey}`}>
                                {renderRow(it.head)}
                                <li
                                    className="notifications-archive__group-more"
                                    onClick={() => toggleGroupExpand(it.groupKey)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleGroupExpand(it.groupKey); }}
                                >
                                    <FormattedMessage
                                        id="notifications.archive.groupMore"
                                        defaultMessage="{count, plural, one {+# more from this conversation} other {+# more from this conversation}}"
                                        values={{ count: extraCount }}
                                    />
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            </div>
        );
    };

    return (
        <div className="notifications-archive">
            <MikaPageHeader
                title={intl.formatMessage({ id: 'notifications.archive.title', defaultMessage: 'Notifications' })}
                subtitle={intl.formatMessage({ id: 'notifications.archive.subtitle', defaultMessage: 'All your notifications in one place' })}
                actions={
                    <div className="notifications-archive__head-actions">
                        <span className="notifications-archive__count">
                            <FormattedMessage
                                id="notifications.archive.unread"
                                defaultMessage="{count, plural, =0 {No unread} one {# unread} other {# unread}}"
                                values={{ count: unreadCount }}
                            />
                        </span>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                className="notifications-archive__mark-all"
                                onClick={handleMarkAllRead}
                            >
                                <FormattedMessage id="notifications.archive.markAllRead" defaultMessage="Mark all as read" />
                            </button>
                        )}
                        <button
                            type="button"
                            className="notifications-archive__settings-btn"
                            onClick={() => history.push('/dashboard/notifications/settings')}
                            aria-label={intl.formatMessage({ id: 'notifications.archive.openSettings', defaultMessage: 'Notification settings' })}
                            title={intl.formatMessage({ id: 'notifications.archive.openSettings', defaultMessage: 'Notification settings' })}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="3" />
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                            </svg>
                        </button>
                    </div>
                }
            />

            <div className="notifications-archive__layout">
                <NotificationSidebar
                    counts={sidebarCounts}
                    status={sidebarStatus}
                    category={sidebarCategory}
                    dateBucket={sidebarDateBucket}
                    onStatusChange={setSidebarStatus}
                    onCategoryChange={setSidebarCategory}
                    onDateBucketChange={setSidebarDateBucket}
                />

                <main className="notifications-archive__main">
                    <div className="notifications-archive__main-head">
                        <label
                            className="notifications-archive__head-master"
                            aria-label={intl.formatMessage({ id: 'notifications.bulk.selectAll', defaultMessage: 'Select all' })}
                            title={intl.formatMessage({ id: 'notifications.bulk.selectAll', defaultMessage: 'Select all' })}
                        >
                            <input
                                ref={headMasterRef}
                                type="checkbox"
                                checked={headMasterChecked}
                                onChange={handleHeadMasterClick}
                                disabled={filteredItems.length === 0}
                            />
                        </label>
                        <NotificationTabs
                            active={tab}
                            counts={tabCounts}
                            onChange={setTab}
                        />
                        <div className="notifications-archive__search">
                            <svg className="notifications-archive__search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="11" cy="11" r="7" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input
                                type="text"
                                className="notifications-archive__search-input"
                                placeholder={intl.formatMessage({ id: 'notifications.archive.searchPlaceholder', defaultMessage: 'Search notifications…' })}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    className="notifications-archive__search-clear"
                                    onClick={() => setSearchInput('')}
                                    aria-label={intl.formatMessage({ id: 'notifications.archive.searchClear', defaultMessage: 'Clear search' })}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="notifications-archive__body">
                        <WebPushBanner />

                        {visiblePending.length > 0 && (
                            <button
                                type="button"
                                className="notifications-archive__new-pill"
                                onClick={handleLoadPending}
                            >
                                <span className="notifications-archive__new-pill-icon" aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="19" x2="12" y2="5" />
                                        <polyline points="5 12 12 5 19 12" />
                                    </svg>
                                </span>
                                <span className="notifications-archive__new-pill-text">
                                    <FormattedMessage
                                        id="notifications.archive.newItems"
                                        defaultMessage="{count, plural, one {# new notification} other {# new notifications}}"
                                        values={{ count: visiblePending.length }}
                                    />
                                </span>
                                <span className="notifications-archive__new-pill-cta">
                                    <FormattedMessage id="notifications.archive.newItemsClick" defaultMessage="Click to view" />
                                </span>
                            </button>
                        )}

                        {selectedItems.length > 0 && (
                            <NotificationBulkToolbar
                                selectedItems={selectedItems}
                                totalVisible={filteredItems.length}
                                onSelectAll={handleSelectAllVisible}
                                onClearSelection={clearSelection}
                                onBulkMarkRead={handleBulkMarkRead}
                                onBulkToggleStar={handleBulkToggleStar}
                                onBulkSnooze={handleBulkSnooze}
                            />
                        )}

                        {showSkeleton && (
                            <div className="notifications-archive__group">
                                <ul className="notifications-archive__list">
                                    {Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} />)}
                                </ul>
                            </div>
                        )}

                        {isEmpty && searchQuery && (
                            <EmptyState
                                messageId="notifications.archive.emptySearch"
                                defaultMessage="No notifications match your search"
                            />
                        )}
                        {isEmpty && !searchQuery && inMutedTab && (
                            <EmptyState
                                messageId="notifications.archive.emptyMuted"
                                defaultMessage="No muted sources. Use the ⋮ menu on a notification to mute its source."
                            />
                        )}
                        {isEmpty && !searchQuery && !inMutedTab && (
                            <EmptyState
                                messageId="notifications.archive.empty"
                                defaultMessage="You have no notifications"
                            />
                        )}

                        {!showSkeleton && totalVisible > 0 && (
                            <>
                                {BUCKET_ORDER.map((bucket) => renderGroup(bucket))}
                            </>
                        )}
                    </div>
                </main>
            </div>

            <NotificationDrawer
                item={selectedItem}
                locale={locale}
                onClose={handleDrawerClose}
            />
        </div>
    );
};

export default NotificationsArchivePage;
